import React, { useState, useEffect } from 'react';
import { useMutation, useQueryClient } from 'react-query';
import {
  Box,
  Button,
  FormControl,
  FormLabel,
  Input,
  useToast,
  Spinner
} from '@chakra-ui/react';
import {
  createAdmin,
  updateAdmin,
  getAdminById
} from '../../../services/adminService';
import CustomSpinner from '../../EndedAuctions/CustomSpinner';

const AdminForm = ({ adminId, onClose }) => {
  const queryClient = useQueryClient();
  const toast = useToast();
  
  const [admin, setAdmin] = useState({
    username: '',
    email: '',
    password: ''
  });

  const fetchAdmin = async (id) => {
    try {
      const data = await getAdminById(id);
      setAdmin(data);
    } catch (error) {
      toast({
        title: 'Error fetching admin',
        description: error.message,
        status: 'error',
        duration: 5000,
        isClosable: true
      });
    }
  };

  useEffect(() => {
    if (adminId) {
      fetchAdmin(adminId);
    }
  }, [adminId]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setAdmin((prev) => ({
      ...prev,
      [name]: value
    }));
  };

  const createMutation = useMutation(createAdmin, {
    onSuccess: () => {
      queryClient.invalidateQueries('admins');
      toast({
        title: 'Admin created.',
        status: 'success',
        duration: 5000,
        isClosable: true
      });
      onClose();
    },
    onError: (error) => {
      toast({
        title: 'Error creating admin',
        description: error.message,
        status: 'error',
        duration: 5000,
        isClosable: true
      });
    }
  });

  const updateMutation = useMutation(updateAdmin, {
    onSuccess: () => {
      queryClient.invalidateQueries('admins');
      toast({
        title: 'Admin updated.',
        status: 'success',
        duration: 5000,
        isClosable: true
      });
      onClose();
    },
    onError: (error) => {
      toast({
        title: 'Error updating admin',
        description: error.message,
        status: 'error',
        duration: 5000,
        isClosable: true
      });
    }
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    if (adminId) {
      updateMutation.mutate({ id: adminId, ...admin });
    } else {
      createMutation.mutate(admin);
    }
  };

  if (adminId && !admin.username && !admin.email && !admin.password) {
    return <CustomSpinner />;
  }

  return (
    <Box as="form" onSubmit={handleSubmit}>
      <FormControl id="username" isRequired>
        <FormLabel>Username</FormLabel>
        <Input
          name="username"
          value={admin.username}
          onChange={handleChange}
          placeholder="Username"
        />
      </FormControl>
      <FormControl id="email" isRequired mt={4}>
        <FormLabel>Email</FormLabel>
        <Input
          name="email"
          value={admin.email}
          onChange={handleChange}
          placeholder="Email"
        />
      </FormControl>
      <FormControl id="password" isRequired mt={4}>
        <FormLabel>Password</FormLabel>
        <Input
          name="password"
          type="password"
          value={admin.password}
          onChange={handleChange}
          placeholder="Password"
        />
      </FormControl>
      <Button mt={4} colorScheme="teal" isLoading={createMutation.isLoading || updateMutation.isLoading} type="submit">
        {adminId ? 'Update Admin' : 'Create Admin'}
      </Button>
    </Box>
  );
};

export default AdminForm;
